import type { LogLevel as ContractLogLevel } from "@shared/contracts";
import { env } from "@/app/config/env";
import { getManifest } from "@/app/config/manifest";
import { loadCrashReporter, platform, type CrashReporterOptions } from "@/app/platform";
import { scrub, scrubString } from "./scrub";

/**
 * THE REPORTING SEAM.
 *
 * Two rules, both from the audit that produced this design:
 *
 * 1. **Reporting is structural, not remembered.** Nothing in a feature calls this directly
 *    for a failed request: the query cache reports every failure and the service reports
 *    every rejected call. A new screen cannot ship unreported.
 * 2. **No provider is load-bearing.** Sentry and PostHog are behind this interface and are
 *    imported ONLY when their keys exist, so a project with no DSN pays nothing — no
 *    network, no bundle on the critical path — and swapping either one touches this folder.
 *
 * On a phone this matters more than on the web: there is no console to open, no network tab,
 * and the person who hit the bug is not going to file a useful report.
 *
 * ## Why the CAPACITOR SDK and not the React one
 *
 * `@sentry/react` alone installs `window.onerror` and `unhandledrejection` handlers inside
 * the web view and stops there. That leaves the whole class of failure that only a phone
 * has completely invisible: a crash in Swift, a watchdog kill during launch, the web view's
 * content process being terminated under memory pressure (which the user experiences as the
 * app vanishing), and any crash occurring before the bundle has executed at all. The
 * Capacitor SDK wraps the React one — hence both, initialised together — and adds the native
 * handlers underneath it. `enableWatchdogTerminationTracking` and `enableAppHangTracking`
 * both default to true and are exactly why this is worth the extra package.
 *
 * ## Three kinds of signal, and they are not interchangeable
 *
 * - `reportError` — something broke. Becomes an issue somebody has to look at.
 * - `log.*`       — a structured line. Searchable, attached to the surrounding error, and
 *                   never an issue on its own. This is what `enableLogs` turns on.
 * - `track`       — a product event, for funnels. Not a diagnostic.
 *
 * Breadcrumbs are the fourth and are automatic: the SDK already records console calls, taps,
 * network requests and navigation, and staples the last hundred onto every error. That is
 * why there is no hand-rolled ring buffer here — it would duplicate a thing that ships on by
 * default.
 */
type Context = Record<string, unknown>;

/**
 * The four verbs come from the wire contract now, because a server value can raise the
 * threshold below — and a vocabulary that exists in two places is one that eventually
 * differs by an entry.
 */
type LogLevel = ContractLogLevel;

interface Reporter {
  error(error: unknown, context?: Context): void;
  message(message: string, context?: Context): void;
  log(level: LogLevel, message: string, context?: Context): void;
  identify(userId: string | null): void;
  track(event: string, properties?: Context): void;
}

const consoleReporter: Reporter = {
  error(error, context) {
    console.error("[report]", error, context ? scrub(context) : "");
  },
  message(message, context) {
    console.warn("[report]", scrubString(message), context ? scrub(context) : "");
  },
  log(level, message, context) {
    console[level === "warn" ? "warn" : level === "error" ? "error" : "info"](
      `[${level}]`,
      scrubString(message),
      context ? scrub(context) : "",
    );
  },
  identify() {},
  track(event, properties) {
    if (!env.isProduction) console.info("[track]", event, properties ? scrub(properties) : "");
  },
};

let reporter: Reporter = consoleReporter;

/**
 * Starts whatever is configured. Safe to call when nothing is: the console reporter stays,
 * which is the honest behaviour for a dev build.
 */
export async function initObservability(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
  if (!dsn && !posthogKey) return;

  /**
   * 🚨 THE CRASH REPORTER COMES THROUGH THE NATIVE SEAM, and it did not always.
   *
   * `@sentry/capacitor` bridges to the native iOS SDK, so it is a Capacitor plugin like any
   * other — and it was imported directly here, past a lint rule whose globs happened not to
   * match its name. `src/app/platform/reporting.ts` now owns the choice between the native
   * bridge and the browser SDK, and tells the whole story.
   *
   * `@sentry/react` stays HERE, because it is a browser SDK with no native half and because
   * every option built below is a reporting-policy decision — the replay rates, the masking,
   * the scrubbers — which belong in this file rather than in the seam.
   */
  const [sentry, sentryReact, posthog] = await Promise.all([
    dsn ? loadCrashReporter() : Promise.resolve(null),
    dsn ? import("@sentry/react") : Promise.resolve(null),
    posthogKey ? import("posthog-js") : Promise.resolve(null),
  ]);

  if (sentry && sentryReact) {
    /**
     * ⚠️ THE OPTION TYPE IS THE BROWSER SDK'S, AND THAT REPLACED A HAND-WIDENED ONE.
     *
     * This used to be `CapacitorOptions` with the two session-replay sample rates added back
     * by hand, because the Capacitor SDK's own type omits both while its `init` spreads
     * whatever it is handed straight into the browser SDK — so they took effect and the type
     * did not know it. Reading the type off the SDK that actually consumes the options says
     * that properly instead of patching around it. See `platform/reporting.ts`.
     */
    const options: CrashReporterOptions = {
      dsn,
      // The release is what makes a report actionable: an installed app can be any version,
      // and "which build was this?" is otherwise unanswerable. It is also the key that
      // joins a JavaScript stack to its source map and a native stack to its dSYM, so it
      // has to be identical in both upload steps.
      release: `${env.appName}@${env.version}`,
      environment: env.appEnv,
      tracesSampleRate: 0,

      /**
       * Structured logs, off by default in the SDK. On here because the alternative on a
       * phone is nothing at all — see `log` below for what may go through it.
       */
      enableLogs: true,

      /**
       * SESSION REPLAY, AND WHY IT IS RECORD-ON-ERROR ONLY.
       *
       * You cannot ask someone to reproduce a bug with logging switched on, so a replay of
       * the seconds before a failure is worth more than any log we could write. But a
       * continuous recording of an app holding personal data is a different proposition
       * from a debugging aid: `replaysSessionSampleRate: 0` means nothing is recorded
       * until something actually breaks, at which point the buffered window is attached to
       * the error.
       *
       * Masking is stated rather than inherited. The defaults are already aggressive, and
       * they are the behaviour we want — but a default that flips in a minor release and
       * silently starts transmitting the text on screen is not a risk worth carrying.
       *
       * Canvas recording is deliberately NOT enabled: it has no personal-data scrubbing
       * at all, so it cannot satisfy the rule in `scrub.ts`.
       *
       * ## Both numbers now come from the manifest, and only one of them is a setting
       *
       * `replaysOnErrorSampleRate` is the billing lever: replay is charged separately, and
       * a spike used to be un-turnable-down without an App Store release. It is a number
       * a deploy can now change.
       *
       * `replaysSessionSampleRate` is the continuous-recording rate, and the manifest is
       * NOT allowed to raise it — `app/config/manifest.ts` clamps it to zero on the way in,
       * whatever the service says. Reading it from the manifest rather than writing `0`
       * here is deliberate: it puts the refusal somewhere a test can hold it, instead of
       * leaving it as a literal that a future edit can quietly replace with a variable.
       *
       * ONE LAUNCH OF LAG, AND IT IS THE SDK'S, NOT OURS. Both rates are fixed when Sentry
       * initialises and cannot be changed afterwards, so a change reaches a phone on its
       * next launch. That is why start-up reads the CACHED manifest before arming reporting
       * (see AppProviders): without it these would read the built-in defaults every single
       * launch and the setting would never take effect at all.
       */
      integrations: [
        sentryReact.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      replaysSessionSampleRate: getManifest().replay.sessionSampleRate,
      replaysOnErrorSampleRate: getManifest().replay.onErrorSampleRate,

      beforeSend(event) {
        return scrub(event) as typeof event;
      },

      /**
       * BREADCRUMBS GO THROUGH THE SAME SCRUBBER AS EVERYTHING ELSE.
       *
       * They are collected automatically — every console call, tap and request — which
       * makes them the most likely place for an email address or a token in a URL to
       * leave the device, and the least likely place for anyone to think to look. The
       * `beforeSend` wall above never sees them, because they are attached to the event
       * after it runs.
       */
      beforeBreadcrumb(breadcrumb) {
        return scrub(breadcrumb) as typeof breadcrumb;
      },

      initialScope: { tags: { platform, appEnv: env.appEnv } },
    };

    /* One argument now: the seam knows whether the native bridge needs the browser SDK's own
       `init` passed through to it, which is the one thing that differs between the two. */
    sentry.init(options);
  }

  posthog?.default.init(posthogKey!, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? "https://eu.i.posthog.com",
    autocapture: false,
    capture_pageview: false,
    // A phone app is not a website: `person_profiles` on every anonymous launch inflates
    // the bill and tells you nothing until someone signs in.
    person_profiles: "identified_only",
  });

  reporter = {
    error(error, context) {
      consoleReporter.error(error, context);
      sentry?.captureException(error, { extra: scrub(context ?? {}) as Context });
    },
    message(message, context) {
      sentry?.captureMessage(scrubString(message), { extra: scrub(context ?? {}) as Context });
    },
    log(level, message, context) {
      consoleReporter.log(level, message, context);
      sentry?.logger[level](scrubString(message), scrub(context ?? {}) as Context);
    },
    identify(userId) {
      sentry?.setUser(userId ? { id: userId } : null);
      if (userId) posthog?.default.identify(userId);
      else posthog?.default.reset();
    },
    track(event, properties) {
      posthog?.default.capture(event, scrub(properties ?? {}) as Context);
    },
  };
}

export function reportError(error: unknown, context?: Context): void {
  reporter.error(error, context);
}

export function reportMessage(message: string, context?: Context): void {
  reporter.message(message, context);
}

/** Lowest first. Nothing here is user-facing; it exists so two levels can be compared. */
const LEVEL_RANK: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

/**
 * WHETHER THIS LINE IS EMITTED, ASKED AT THE MOMENT IT IS WRITTEN.
 *
 * Read from the manifest per call rather than captured once, so a threshold that changes
 * while the app is open takes effect on the next line instead of the next launch — which on
 * a phone can be days away.
 */
function emit(level: LogLevel, message: string, context?: Context): void {
  if (LEVEL_RANK[level] < LEVEL_RANK[getManifest().logLevel]) return;
  reporter.log(level, message, context);
}

/**
 * STRUCTURED LINES, AND THE THRESHOLD IS THE SERVICE'S TO MOVE.
 *
 * Deliberately the same four verbs as the service's logger, so one habit works on both
 * sides of the wire.
 *
 * ## The audit finding this is one careful step away from
 *
 * The sharpest observability finding behind this repo's rules was a logger whose `info` and
 * `debug` were replaced by no-ops unless a flag was set: the only signal that existed was
 * switched off in exactly the environment that needed it, and a failed delivery at 3am left
 * nothing behind. "Never no-op a log level" comes from there, and a threshold is visibly in
 * the same family.
 *
 * Three things make this the cure rather than the disease, and all three are load-bearing:
 *
 *   1. **The default emits everything.** `logLevel` defaults to `debug` in the app, in the
 *      service, and in the cached copy. An app that has never reached the service, cannot
 *      parse the answer, or is running against a service that has never heard of this
 *      variable logs exactly what it logged before this existed. Silence is never what you
 *      get by forgetting something.
 *   2. **It is remote, so it is reversible in seconds.** The audited logger was a build-time
 *      flag, which is why the failure was permanent for everyone who had installed it. Here
 *      the direction that matters most is UP: turning a shipped build all the way to `debug`
 *      for the person having the problem right now is the thing that was impossible before.
 *   3. **`error` cannot be silenced.** It is the highest rank, so no threshold suppresses it,
 *      and errors somebody has to fix go through `reportError` — a different path entirely,
 *      which this does not touch.
 *
 * This is NOT `reportError`. A log line is searchable context that gets attached to whatever
 * error happens near it; `reportError` is a thing somebody has to fix. Logging an error you
 * have already reported files it twice.
 */
export const log = {
  debug: (message: string, context?: Context) => emit("debug", message, context),
  info: (message: string, context?: Context) => emit("info", message, context),
  warn: (message: string, context?: Context) => emit("warn", message, context),
  error: (message: string, context?: Context) => emit("error", message, context),
};

/** Ties reports to a user without ever sending their email or name. */
export function identifyUser(userId: string | null): void {
  reporter.identify(userId);
}

/**
 * Product events. Funnels only — the steps of a flow that matters, entered / completed /
 * abandoned. Not every tap: an event nobody has a question about is a cost with no answer.
 */
export function track(event: string, properties?: Context): void {
  reporter.track(event, properties);
}
